// frontend/src/pages/CopyrightPolicyPage.tsx
import { InfoPageLayout } from './InfoPageLayout';

export function CopyrightPolicyPage() {
  return (
    <InfoPageLayout title="Copyright Policy" documentTitle="Copyright" metaDescription="Llambada copyright and DMCA policy — how we handle intellectual property claims, counter-notifications, and AI-generated code considerations.">
      <p>Last Updated: February 3, 2026</p>

      <h2>Respect for Intellectual Property</h2>
      <p>
        <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline">ChapterPal Inc</a>. ("we," "our," or "us") respects the intellectual property rights of others 
        and expects all users of Llambada (the "Service") to do the same. We will respond to notices 
        of alleged copyright infringement that comply with applicable law.
      </p>

      <h2>Reporting Copyright Infringement</h2>
      <p>
        If you believe that any Mini-App, code, content, or other material on the Service infringes 
        your copyright, please send a notice to our designated agent with the following information:
      </p>
      <ol>
        <li>
          A physical or electronic signature of the copyright owner or a person authorized to act 
          on their behalf
        </li>
        <li>
          Identification of the copyrighted work claimed to be infringed (e.g., a description of 
          your original code, application, or content)
        </li>
        <li>
          Identification of the allegedly infringing material on our Service, including the URL 
          or other specific location where the material can be found
        </li>
        <li>
          Your contact information, including your address, telephone number, and email address
        </li>
        <li>
          A statement that you have a good faith belief that use of the material in the manner 
          complained of is not authorized by the copyright owner, its agent, or the law
        </li>
        <li>
          A statement, made under penalty of perjury, that the information in the notification 
          is accurate and that you are the copyright owner or authorized to act on the owner's behalf
        </li>
      </ol>

      <h2>Designated Agent</h2>
      <p>
        Please send copyright infringement notices to our designated agent:
      </p>
      <p>
        <strong>By Email:</strong><br />
        <a href="mailto:copyright@llambada.com">copyright@llambada.com</a>
      </p>
      <p>
        <strong>By Mail:</strong><br />
        <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline">ChapterPal Inc</a>.<br />
        Copyright Agent<br />
        410-117 Quai St-André<br />
        Québec, QC<br />
        Canada
      </p>

      <h2>Our Response to Infringement Claims</h2>
      <p>
        Upon receiving a valid notice of claimed infringement, we may take the following actions:
      </p>
      <ul>
        <li>Remove or disable access to the allegedly infringing material</li>
        <li>Notify the user who posted the material that it has been removed</li>
        <li>In appropriate circumstances, terminate the accounts of repeat infringers</li>
      </ul>

      <h2>Counter-Notification</h2>
      <p>
        If you believe that material you posted was removed or disabled by mistake or 
        misidentification, you may submit a counter-notification to our designated agent 
        containing:
      </p>
      <ol>
        <li>Your physical or electronic signature</li>
        <li>
          Identification of the material that was removed and the location where it appeared 
          before removal
        </li>
        <li>
          A statement under penalty of perjury that you have a good faith belief that the 
          material was removed or disabled as a result of mistake or misidentification
        </li>
        <li>
          Your name, address, and telephone number, and a statement that you consent to the 
          jurisdiction of the federal courts in your district and that you will accept service 
          of process from the person who provided the original notification
        </li>
      </ol>
      <p>
        Upon receiving a valid counter-notification, we may restore the removed material unless 
        the original complainant notifies us that they have filed a court action seeking to 
        restrain you from the allegedly infringing activity.
      </p>

      <h2>Repeat Infringers</h2>
      <p>
        In accordance with our Terms of Service, we may terminate the accounts of users who 
        are determined to be repeat infringers. We consider factors such as the number of valid 
        infringement notices received and whether the user has submitted valid counter-notifications.
      </p>

      <h2>Special Considerations for AI-Generated Code</h2>
      <p>
        Mini-Apps created through our Service contain AI-generated code. We acknowledge that:
      </p>
      <ul>
        <li>
          AI-generated code may inadvertently resemble existing copyrighted code
        </li>
        <li>
          Users are responsible for reviewing AI-generated code for potential infringement 
          before publishing
        </li>
        <li>
          We will investigate claims of infringement involving AI-generated code and take 
          appropriate action
        </li>
      </ul>
      <p>
        If you believe AI-generated code in a Mini-App infringes your copyright, please follow 
        the reporting process above. Include information about the original copyrighted work 
        and how the AI-generated code allegedly infringes it.
      </p>

      <h2>Good Faith Requirement</h2>
      <p>
        Please note that under applicable law, a person who knowingly materially misrepresents 
        that material or activity is infringing may be subject to liability. If you are unsure 
        whether material infringes your copyright, you may wish to consult with an attorney.
      </p>

      <h2>Modifications to This Policy</h2>
      <p>
        We may update this Copyright Policy from time to time. Changes will be posted on this 
        page with an updated revision date.
      </p>
    </InfoPageLayout>
  );
}
